import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DotsSixVertical, Check } from '@phosphor-icons/react';
import api from '../services/api';
import { KNOWN_SECTIONS, formatSectionLabel } from '../utils/sections';

// Extract section data from an analysis's raw_response
function getSectionData(rawResponse, sectionKey) {
  if (!rawResponse) return null;

  if (sectionKey === 'core') {
    const core = {};
    Object.entries(rawResponse).forEach(([key, value]) => {
      if (value === null || value === undefined || Array.isArray(value) || typeof value !== 'object') {
        core[key] = value;
      }
    });
    return core;
  }

  return rawResponse[sectionKey] ?? null;
}

// Detect which sections are available across all analyses
function detectSections(analyses) {
  const sectionSet = new Set(['core']);
  analyses.forEach(a => {
    const raw = a.raw_response;
    if (!raw) return;
    KNOWN_SECTIONS.forEach(key => {
      if (raw[key] != null) sectionSet.add(key);
    });
  });
  return Array.from(sectionSet).map(key => ({
    key,
    label: formatSectionLabel(key),
  }));
}

// Sortable card for drag-and-drop ranking
function SortableAnalysisCard({ id, analysis, rank, sectionData, isRanked }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 'auto',
  };

  const version = analysis.version || '?';
  const model = analysis.model_used || 'Unknown model';
  const prompt = analysis.prompt_name;

  return (
    <div ref={setNodeRef} style={style} className={`compare-card ${isDragging ? 'compare-card-dragging' : ''} ${!isRanked ? 'compare-card-unranked' : ''}`}>
      <div className="compare-card-header">
        <div className="compare-card-drag" {...attributes} {...listeners}>
          <DotsSixVertical size={16} />
        </div>
        <span className="compare-card-rank">{isRanked ? `#${rank}` : '\u2014'}</span>
        <div className="compare-card-meta">
          <span className="compare-card-model">{model}</span>
          {prompt && <span className="compare-card-prompt">{prompt}</span>}
          <span className="compare-card-version">v{version}</span>
        </div>
      </div>
      <div className="compare-card-body">
        {sectionData ? (
          <pre className="compare-card-json">{JSON.stringify(sectionData, null, 2)}</pre>
        ) : (
          <p className="compare-card-empty">No data for this section</p>
        )}
      </div>
    </div>
  );
}

export default function AnalysisCompare({ item, onClose }) {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeDragId, setActiveDragId] = useState(null);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [activeSection, setActiveSection] = useState('core');
  const [notes, setNotes] = useState('');

  // section_rankings: { sectionKey: [analysisId, ...] }
  const [sectionRankings, setSectionRankings] = useState({});

  // Default display order (by version ascending) — used as fallback for unranked sections
  const [defaultOrder, setDefaultOrder] = useState([]);

  // Baseline for change detection
  const [baseline, setBaseline] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const sections = useMemo(() => detectSections(analyses), [analyses]);

  // Map analysis_id -> analysis object for quick lookup
  const analysisMap = useMemo(
    () => Object.fromEntries(analyses.map(a => [a.analysis_id, a])),
    [analyses]
  );

  // Ordered analysis IDs for the current section
  const currentOrder = useMemo(() => {
    return sectionRankings[activeSection] || defaultOrder;
  }, [sectionRankings, activeSection, defaultOrder]);

  const hasChanges = useMemo(() => {
    if (!baseline) return false;
    return (
      JSON.stringify(sectionRankings) !== JSON.stringify(baseline.sectionRankings) ||
      notes !== baseline.notes
    );
  }, [sectionRankings, notes, baseline]);

  // Load analyses and existing rankings
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await api.getItemAnalyses(item.id, { includeRawResponse: true });
        if (!resp || !Array.isArray(resp.analyses)) {
          throw new Error('Unexpected API response: missing analyses array');
        }
        const loadedAnalyses = resp.analyses;
        setAnalyses(loadedAnalyses);

        // Default order: by version ascending (spread to avoid mutating state array)
        const defaultOrder = [...loadedAnalyses]
          .sort((a, b) => (a.version || 0) - (b.version || 0))
          .map(a => a.analysis_id);

        // Load existing ranking (separate try/catch so analyses still work if ranking fetch fails)
        let initialRankings = {};
        let initialNotes = '';
        try {
          const existingRanking = await api.getRanking(item.id);
          if (existingRanking) {
            initialRankings = existingRanking.section_rankings || {};
            initialNotes = existingRanking.notes || '';
          }
        } catch (rankErr) {
          setError(
            'Warning: Could not load saved rankings (' + rankErr.message + '). ' +
            'Showing default ordering. Saving will overwrite any previously saved rankings.'
          );
        }

        // Reconcile saved rankings against current analyses (remove stale IDs, append new ones)
        const currentIdSet = new Set(defaultOrder);
        let staleCount = 0;
        for (const [section, ids] of Object.entries(initialRankings)) {
          const filtered = ids.filter(id => currentIdSet.has(id));
          staleCount += ids.length - filtered.length;
          const missing = defaultOrder.filter(id => !ids.includes(id));
          initialRankings[section] = [...filtered, ...missing];
        }
        if (staleCount > 0) {
          setError(
            `${staleCount} previously ranked analysis ID(s) no longer exist and were removed. ` +
            'Save to update your rankings.'
          );
        }

        setDefaultOrder(defaultOrder);
        setSectionRankings(initialRankings);
        setNotes(initialNotes);
        setBaseline({
          sectionRankings: structuredClone(initialRankings),
          notes: initialNotes,
        });
      } catch (err) {
        setError('Failed to load analyses: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [item.id]);

  // Guard against losing unsaved ranking changes on tab close/navigation
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges]);

  // Auto-hide success
  useEffect(() => {
    if (successMsg) {
      const t = setTimeout(() => setSuccessMsg(null), 3000);
      return () => clearTimeout(t);
    }
  }, [successMsg]);

  const handleClose = useCallback(() => {
    if (hasChanges && !window.confirm('You have unsaved ranking changes. Discard and go back?')) return;
    onClose();
  }, [hasChanges, onClose]);

  const handleDragStart = useCallback((event) => {
    setActiveDragId(event.active.id);
  }, []);

  const handleDragEnd = useCallback((event) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setSectionRankings(prev => {
      const order = prev[activeSection] || [...defaultOrder];
      const oldIdx = order.indexOf(active.id);
      const newIdx = order.indexOf(over.id);
      if (oldIdx === -1 || newIdx === -1) return prev;
      return { ...prev, [activeSection]: arrayMove(order, oldIdx, newIdx) };
    });
  }, [activeSection, defaultOrder]);

  const acceptCurrentOrder = useCallback(() => {
    setSectionRankings(prev => ({
      ...prev,
      [activeSection]: prev[activeSection] || [...defaultOrder],
    }));
  }, [activeSection, defaultOrder]);

  // Whether the active section has an explicit ranking
  const isActiveRanked = !!sectionRankings[activeSection];

  const saveRankings = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSuccessMsg(null);

    try {
      const analysisIds = analyses.map(a => a.analysis_id);
      await api.saveRanking(item.id, {
        analysis_ids: analysisIds,
        section_rankings: sectionRankings,
        notes: notes || null,
      });
      setBaseline({
        sectionRankings: structuredClone(sectionRankings),
        notes,
      });
      setSuccessMsg('Rankings saved');
    } catch (err) {
      setError('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  }, [item.id, analyses, sectionRankings, notes]);

  if (loading) {
    return (
      <div className="compare-container">
        <div className="compare-loading">Loading analyses...</div>
      </div>
    );
  }

  if (analyses.length < 2) {
    return (
      <div className="compare-container">
        <div className="compare-empty">
          <p className="compare-empty-title">
            {analyses.length === 0 ? 'No analyses found' : 'Only one analysis version'}
          </p>
          <p className="compare-empty-desc">
            This item needs at least 2 analysis versions to compare and rank.
            Open the item and use Re-analyze on its Versions tab to create more versions.
          </p>
          <button onClick={handleClose} className="btn btn-secondary">Back to Review</button>
        </div>
      </div>
    );
  }

  return (
    <div className="compare-container">
      {/* Header */}
      <div className="compare-header">
        <div className="compare-header-left">
          <h2 className="compare-title">Compare & Rank</h2>
          <span className="compare-count">{analyses.length} analyses</span>
        </div>
        <div className="compare-header-right">
          {hasChanges && <span className="review-unsaved-badge">Unsaved changes</span>}
          <button onClick={handleClose} className="btn btn-secondary">Back to Review</button>
          <button
            onClick={saveRankings}
            disabled={saving || !hasChanges || Object.keys(sectionRankings).length === 0}
            className="btn btn-primary"
          >
            {saving ? 'Saving...' : 'Save Rankings'}
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && <div className="compare-msg compare-msg-error">{error}</div>}
      {successMsg && <div className="compare-msg compare-msg-success">{successMsg}</div>}

      {/* Section tabs */}
      <div className="compare-tabs">
        {sections.map(s => (
          <button
            key={s.key}
            className={`review-tab ${activeSection === s.key ? 'review-tab-active' : ''}`}
            onClick={() => setActiveSection(s.key)}
          >
            {sectionRankings[s.key] && (
              <Check className="compare-tab-check" size={14} />
            )}
            {s.label}
          </button>
        ))}
      </div>

      {/* Instructions */}
      {isActiveRanked ? (
        <div className="compare-instructions">
          Drag cards to rank by quality. #1 = best.
        </div>
      ) : (
        <div className="compare-instructions compare-instructions-unranked">
          <strong>Not yet ranked.</strong> Drag cards to set ranking, or{' '}
          <button className="compare-accept-btn" onClick={acceptCurrentOrder}>
            accept current order
          </button>
        </div>
      )}

      {/* Scrollable content area for cards + notes */}
      <div className="compare-content">
        {/* Sortable cards */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={currentOrder} strategy={verticalListSortingStrategy}>
            <div className="compare-cards">
              {currentOrder.map((id, idx) => {
                const analysis = analysisMap[id];
                if (!analysis) {
                  return (
                    <div key={id} className="compare-card compare-card-missing">
                      <div className="compare-card-header">
                        <span className="compare-card-rank">#{idx + 1}</span>
                        <span className="compare-card-prompt">Analysis removed or unavailable</span>
                      </div>
                    </div>
                  );
                }
                return (
                  <SortableAnalysisCard
                    key={id}
                    id={id}
                    analysis={analysis}
                    rank={idx + 1}
                    sectionData={getSectionData(analysis.raw_response, activeSection)}
                    isRanked={isActiveRanked}
                  />
                );
              })}
            </div>
          </SortableContext>
          <DragOverlay>
            {activeDragId && analysisMap[activeDragId] ? (
              <div className="compare-card compare-card-dragging">
                <div className="compare-card-header">
                  <span className="compare-card-rank">
                    #{currentOrder.indexOf(activeDragId) + 1}
                  </span>
                  <div className="compare-card-meta">
                    <span className="compare-card-model">
                      {analysisMap[activeDragId].model_used || 'Unknown model'}
                    </span>
                    <span className="compare-card-version">
                      v{analysisMap[activeDragId].version || '?'}
                    </span>
                  </div>
                </div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>

        {/* Notes */}
        <div className="compare-notes">
          <label htmlFor="ranking-notes" className="review-field-name">Session Notes</label>
          <textarea
            id="ranking-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Add notes about this ranking session..."
            className="review-input review-textarea"
          />
        </div>
      </div>
    </div>
  );
}
